import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Snackbar } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import theme from './theme/muiTheme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PushNotificationProvider } from './contexts/PushNotificationContext';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import Landing from './pages/Landing/Landing';
import Auth from './pages/Auth/Auth';
import Dashboard from './pages/Dashboard/Dashboard';
import Network from './pages/Network/Network';
import Profile from './pages/Profile/Profile';
import Splash from './pages/Splash/Splash';
import PrivateRoute from './components/common/PrivateRoute';
import ProfileView from './pages/Profile/ProfileView';
import JoinEvent from './pages/EventGallery/JoinEvent';
import PublicQrRedirect from './pages/EventGallery/PublicQrRedirect';
import EventPage from './pages/EventGallery/EventPage';
import Memory from './pages/Memory/Memory';
import Moments from './pages/Moments/Moments';
import Layout from './components/common/Layout';
import OrganisationPage from './pages/Organisation/OrganisationPage';
import SharePage from './pages/Share/SharePage';
import UploadLinkPage from './pages/UploadLink/UploadLinkPage';
import SyncWorkerPage from './pages/SyncWorker/SyncWorkerPage';
import InvitationsHub from './pages/Invitations/InvitationsHub';
import CreateInvitation from './pages/Invitations/CreateInvitation';
import ViewInvitation from './pages/Invitations/ViewInvitation';
import InviteEventView from './pages/Invitations/InviteEventView';
import EC2WakeOverlay from './components/common/EC2WakeOverlay';
import PwaInstallPrompt from './components/common/PwaInstallPrompt';
import OpenInAppBanner from './components/common/OpenInAppBanner';

const EntryRoute = ({ isPWA }) => {
  const { user, token, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (token && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return isPWA ? <Splash /> : <Landing />;
};

const BackExitGuard = ({ isPWA }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [exitToastOpen, setExitToastOpen] = useState(false);
  const exitingRef = useRef(false);
  const guardedLocationKeyRef = useRef(null);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!isPWA || exitingRef.current) {
      return undefined;
    }

    if (guardedLocationKeyRef.current === location.key) {
      return undefined;
    }

    guardedLocationKeyRef.current = location.key;

    const guardState = {
      ...(window.history.state || {}),
      pwaBackExitGuard: true
    };

    window.history.replaceState(guardState, '', window.location.href);
    window.history.pushState(guardState, '', window.location.href);

    const handlePopState = () => {
      if (exitingRef.current) {
        return;
      }

      window.history.pushState(guardState, '', window.location.href);
      exitingRef.current = true;
      setExitToastOpen(true);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isPWA, location.key]);

  // Native app case: Capacitor's hardware back button never touches the
  // WebView's own history/popstate - by default it just finishes the
  // Activity outright, which is the "back button closes the app" behavior
  // this fixes. Mirrors the PWA guard above: navigate back within the SPA
  // when there's somewhere to go, otherwise prompt "press back again to
  // exit" and only actually exit on a second press.
  useEffect(() => {
    if (!isNative) {
      return undefined;
    }

    let listenerHandle;
    let cancelled = false;

    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        navigate(-1);
        return;
      }

      if (exitingRef.current) {
        CapacitorApp.exitApp();
        return;
      }

      exitingRef.current = true;
      setExitToastOpen(true);
      setTimeout(() => {
        exitingRef.current = false;
      }, 2500);
    }).then((handle) => {
      if (cancelled) {
        handle.remove();
      } else {
        listenerHandle = handle;
      }
    });

    return () => {
      cancelled = true;
      listenerHandle?.remove();
    };
  }, [isNative, navigate]);

  const handleToastClose = () => {
    setExitToastOpen(false);
  };

  if (!isPWA && !isNative) {
    return null;
  }

  return (
    <Snackbar
      open={exitToastOpen}
      autoHideDuration={3500}
      onClose={handleToastClose}
      message="Press Back once more to close Circle Moments."
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
  );
};

// Bridges api.js's 'cm:session-expired' event (fired on a 401 instead of a
// window.location.href hard redirect - see api.js's own comment) to a proper
// SPA navigation. `replace: true` so the forced auth page doesn't itself
// become a dead-end back-button stop - pressing back from it goes to
// wherever the user actually was before the session expired.
const SessionExpiredRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleSessionExpired = () => navigate('/auth', { replace: true });
    window.addEventListener('cm:session-expired', handleSessionExpired);
    return () => window.removeEventListener('cm:session-expired', handleSessionExpired);
  }, [navigate]);

  return null;
};

// Native only: routes an Android App Link (www.circlemoments.com/...) into
// the SPA. The WebView here loads the bundled www build, not the live site,
// so simply following the link would land on the app's own "/" - this reads
// the actual target path back out and navigates there instead. Two separate
// paths because @capacitor/app's AppPlugin only fires 'appUrlOpen' from
// onNewIntent (app already running); a cold start's launch intent has to be
// read explicitly via getLaunchUrl() (see AppPlugin.java's handleOnNewIntent
// vs getLaunchUrl).
const AppLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return undefined;
    }

    const goToUrl = (urlString) => {
      try {
        const url = new URL(urlString);
        navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
      } catch {
        // Not a parseable http(s) URL - nothing to route to.
      }
    };

    CapacitorApp.getLaunchUrl().then((result) => {
      if (result?.url) {
        goToUrl(result.url);
      }
    });

    let listenerHandle;
    let cancelled = false;
    CapacitorApp.addListener('appUrlOpen', ({ url }) => goToUrl(url)).then((handle) => {
      if (cancelled) {
        handle.remove();
      } else {
        listenerHandle = handle;
      }
    });

    return () => {
      cancelled = true;
      listenerHandle?.remove();
    };
  }, [navigate]);

  return null;
};

const App = () => {
  const [isPWA, setIsPWA] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if the app is running as a PWA
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const standalone = window.navigator.standalone;
    const androidApp = document.referrer.includes('android-app://');
    
    const isPWAMode = mediaQuery.matches || standalone || androidApp;
    
    console.log('PWA Detection:', {
      mediaQuery: mediaQuery.matches,
      standalone,
      androidApp,
      isPWAMode
    });
    
    setIsPWA(isPWAMode);
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return null; // or a loading spinner
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <AuthProvider>
          <PushNotificationProvider isPWA={isPWA}>
          <AppProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <BackExitGuard isPWA={isPWA} />
              <SessionExpiredRedirect />
              <AppLinkHandler />
              <OpenInAppBanner />
              <EC2WakeOverlay />
              <PwaInstallPrompt isPWA={isPWA} />
              <Routes>
                <Route 
                  path="/" 
                  element={<EntryRoute isPWA={isPWA} />} 
                />
                <Route path="/auth" element={<Auth />} />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/network"
                  element={
                    <PrivateRoute>
                      <Network />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  }
                />
                <Route path="/profile/:username" element={<Layout><ProfileView /></Layout>} />
                <Route path="/join-event" element={<Layout><JoinEvent /></Layout>} />
                <Route path="/join-event/:code" element={<Layout><JoinEvent /></Layout>} />
                <Route path="/public_qr" element={<PublicQrRedirect />} />
                <Route path="/event-deletion-request" element={<Layout><JoinEvent initialMode="deletion" /></Layout>} />
                <Route path="/event-deletion-request/:code" element={<Layout><JoinEvent initialMode="deletion" /></Layout>} />
                <Route
                  path="/moments"
                  element={
                    <PrivateRoute>
                      <Moments />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/memory"
                  element={
                    <PrivateRoute>
                      <Memory />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/event/:event_id"
                  element={
                    <PrivateRoute>
                      <EventPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <PrivateRoute>
                      <NotificationsPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/organisations/:org_id"
                  element={
                    <PrivateRoute>
                      <OrganisationPage />
                    </PrivateRoute>
                  }
                />
                <Route path="/share/:token" element={<Layout><SharePage /></Layout>} />
                <Route path="/upload/:token" element={<Layout><UploadLinkPage /></Layout>} />
                {/* No <Layout> here - this route only renders inside the native
                    Android wrapper's headless sync-worker WebView (see
                    cm-app-packaging's FolderSyncForegroundService), never a
                    real visible tab - SyncWorkerPage itself renders nothing. */}
                <Route path="/sync-worker" element={<SyncWorkerPage />} />
                <Route
                  path="/invitations"
                  element={
                    <PrivateRoute>
                      <InvitationsHub />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/invitations/create"
                  element={
                    <PrivateRoute>
                      <CreateInvitation />
                    </PrivateRoute>
                  }
                />
                <Route path="/invite/view/:invitation_id" element={<Layout><ViewInvitation /></Layout>} />
                <Route path="/invite/event/:invitation_id" element={<Layout><InviteEventView /></Layout>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
          </AppProvider>
          </PushNotificationProvider>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
};

export default App;
